<template>
  <div class="card p-6 mb-2">
    <b-overlay :show="isLoading">
      <div class="d-flex align-items-center mb-5" style="gap: 12px">
        <h6 class="mb-0">Zimmerkontingente Plan</h6>
        <b-button
          variant="outline-primary"
          size="sm"
          class="zimmerkontingent-add"
          @click="addPlanzimmerkontingentRow"
        >
          <Plus :size="16" />
          Zimmertyp hinzufügen
        </b-button>
      </div>

      <div v-if="editedSeason.planZimmerkontingente?.length > 0" class="zimmerkontingent-table">
        <div class="zimmerkontingent-head">
          <div>Zimmertyp</div>
          <div>Anzahl</div>
          <div>Teilt Kontingent mit</div>
          <div></div>
        </div>
        <div
          v-for="(zimmerkontingent, index) in editedSeason.planZimmerkontingente"
          :key="'planzimmerkontingent-' + index"
          class="zimmerkontingent-row"
        >
          <div>
            <AppSelect
              :value="zimmerkontingent.zimmertyp"
              :options="getAvailableZimmertypOptions(zimmerkontingent.zimmertyp)"
              label="Zimmertyp"
              placeholder="Zimmertyp wählen"
              class="zimmerkontingent-select"
              @input="onZimmertypInput(zimmerkontingent, $event)"
            />
          </div>
          <div>
            <AppSelect
              :value="zimmerkontingent.anzahlEinheiten"
              :options="anzahlSelectOptions"
              label="Anzahl"
              placeholder="–"
              hide-search
              list-max-height="200px"
              class="zimmerkontingent-anzahl"
              @input="onAnzahlEinheitenInput(zimmerkontingent, $event)"
            />
          </div>
          <div>
            <AppSelect
              v-if="!zimmerkontingent.teiltkontingentMit"
              :value="null"
              :options="getAvailableZimmertypOptions(zimmerkontingent.teiltkontingentMit)"
              label="Kontingent teilen"
              placeholder="Kontingent teilen …"
              class="zimmerkontingent-select"
              @input="onTeiltKontingentInput(zimmerkontingent, $event)"
            />
            <div v-else class="zimmerkontingent-share">
              <span class="zimmerkontingent-share-code">{{ zimmerkontingent.teiltkontingentMit }}</span>
              <span class="zimmerkontingent-share-label">teilt</span>
              <AppSelect
                v-model="zimmerkontingent.anzahlGeteiltesKontingent"
                :options="getGeteiltesKontingentOptions(zimmerkontingent)"
                label="Geteilte Anzahl"
                hide-search
                size="sm"
                class="zimmerkontingent-share-count"
              />
              <button
                type="button"
                class="zimmerkontingent-share-x"
                title="Teilung entfernen"
                @click="clearTeiltKontingent(zimmerkontingent)"
              >
                <X :size="14" />
              </button>
            </div>
          </div>
          <div class="zimmerkontingent-actions">
            <button
              type="button"
              class="zimmerkontingent-icon-btn"
              title="Zimmertyp entfernen"
              @click="removePlanzimmerkontingentRow(index)"
            >
              <Trash2 :size="16" />
            </button>
          </div>
        </div>
      </div>
      <div v-else class="zimmerkontingent-empty">Noch keine Zimmerkontingente angelegt.</div>

      <div class="d-flex align-items-center mt-4" style="gap: 8px">
        <b-button
          v-if="showPlanZimmerkontingenteActions"
          class="dialog-btn-secondary"
          @click="cancelPlanZimmerkontingente"
        >
          Abbrechen
        </b-button>
        <b-button
          v-if="showPlanZimmerkontingenteActions"
          class="save-btn dialog-btn"
          :disabled="!canSavePlanZimmerkontingente"
          :variant="canSavePlanZimmerkontingente ? 'primary' : 'secondary'"
          @click="savePlanZimmerkontingente"
        >
          Speichern
        </b-button>
      </div>
    </b-overlay>

    <DeleteConfirmationModal
      id="modalZimmertypLoeschen"
      title="Zimmertyp löschen?"
      entityBezeichnung="den Zimmertyp"
      :label="pendingRemoveLabel"
      @confirm="confirmRemovePlanzimmerkontingentRow"
    />
  </div>
</template>

<script>
import AppSelect from '@/components/common/app-select/app-select.vue';
import { Plus, Trash2, X } from 'lucide-vue';
import zimmertypenJson from '@/core/common/helpers/zimmertypen.json';
import * as zimmerkontingenteService from './services/plan-zimmerkontingente.service';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';

export default {
  name: 'ReisenMetadataCardZimmerkontingente',
  components: {
    AppSelect,
    Plus,
    Trash2,
    X,
    DeleteConfirmationModal,
  },
  props: {
    editedSeason: { type: Object, required: true },
    activeMetadata: { type: Object, default: null },
    reise: { type: Object, default: null },
    activeSaisonGueltigAb: { type: String, default: null },
  },
  data() {
    return {
      zimmertypenJson,
      anzahlOptions: Array.from({ length: 16 }, (_, i) => i + 1),
      isLoading: false,
      isEditing: false,
      // gemerkter Index/Label für die Löschbestätigung bereits gespeicherter Zimmertypen
      pendingRemoveIndex: null,
      pendingRemoveLabel: '',
    };
  },
  computed: {
    // Die Anzahl sind Zahlen, als Optionstext werden sie in Strings gewandelt
    anzahlSelectOptions() {
      return this.anzahlOptions.map(anzahl => ({ value: anzahl, text: String(anzahl) }));
    },
    zimmertypOptions() {
      return Object.entries(this.zimmertypenJson)
        .map(([code, name]) => ({ value: code, text: `${code} – ${name}` }))
        .sort((a, b) => a.value.localeCompare(b.value));
    },
    usedZimmertypen() {
      const set = new Set();
      (this.editedSeason.planZimmerkontingente ?? []).forEach(zimmerkontingent => {
        if (zimmerkontingent.zimmertyp) set.add(zimmerkontingent.zimmertyp);
        if (zimmerkontingent.teiltkontingentMit) set.add(zimmerkontingent.teiltkontingentMit);
      });
      return set;
    },
    // Ermittelt neu anzulegende, geänderte und entfernte Zimmerkontingente im Vergleich zum geladenen Stand
    planZimmerkontingenteDiff() {
      const edited = this.editedSeason.planZimmerkontingente ?? [];
      const original = this.activeMetadata?.planZimmerkontingente ?? [];
      const toCreate = edited.filter(
        zimmerkontingent =>
          !zimmerkontingent.id &&
          zimmerkontingent.zimmertyp != null &&
          zimmerkontingent.anzahlEinheiten != null
      );
      const toUpdate = edited.filter(zimmerkontingent => {
        if (!zimmerkontingent.id) return false;
        const originalZimmerkontingent = original.find(item => item.id === zimmerkontingent.id);
        if (!originalZimmerkontingent) return false;
        return (
          originalZimmerkontingent.zimmertyp !== zimmerkontingent.zimmertyp ||
          +originalZimmerkontingent.anzahlEinheiten !== +zimmerkontingent.anzahlEinheiten ||
          (originalZimmerkontingent.anzahlGeteiltesKontingent ?? null) !==
            (zimmerkontingent.anzahlGeteiltesKontingent ?? null) ||
          (originalZimmerkontingent.teiltkontingentMit ?? null) !==
            (zimmerkontingent.teiltkontingentMit ?? null)
        );
      });
      const editedIds = edited.filter(zimmerkontingent => zimmerkontingent.id).map(item => item.id);
      const toDelete = original.filter(item => !editedIds.includes(item.id));
      return { toCreate, toUpdate, toDelete };
    },
    planZimmerkontingenteChanged() {
      const { toCreate, toUpdate, toDelete } = this.planZimmerkontingenteDiff;
      return toCreate.length > 0 || toUpdate.length > 0 || toDelete.length > 0;
    },
    // Alle Zeilen müssen vollständig ausgefüllt sein; bei geteiltem Kontingent auch die geteilte Anzahl
    planZimmerkontingenteValid() {
      return (this.editedSeason.planZimmerkontingente ?? []).every(
        zimmerkontingent =>
          zimmerkontingent.zimmertyp != null &&
          zimmerkontingent.anzahlEinheiten != null &&
          (!zimmerkontingent.teiltkontingentMit || zimmerkontingent.anzahlGeteiltesKontingent != null)
      );
    },
    // Speichern ist nur möglich, wenn es Änderungen gibt UND alle Zeilen vollständig ausgefüllt sind
    canSavePlanZimmerkontingente() {
      return this.planZimmerkontingenteChanged && this.planZimmerkontingenteValid;
    },
    // anzeige der Buttons beim Hinzufügen einer neuen Zeile oder beim Bearbeiten bestehender Zimmertypen
    showPlanZimmerkontingenteActions() {
      return this.isEditing || this.planZimmerkontingenteChanged;
    },
  },
  methods: {
    // Liefert die Zimmertyp-Optionen ohne bereits belegte Typen
    getAvailableZimmertypOptions(currentValue) {
      return this.zimmertypOptions.filter(
        option => !this.usedZimmertypen.has(option.value) || option.value === currentValue
      );
    },
    zimmertypOption(code) {
      if (!code) return null;
      return this.zimmertypOptions.find(option => option.value === code) ?? { value: code, text: code };
    },
    onZimmertypInput(zimmerkontingent, zimmertyp) {
      zimmerkontingent.zimmertyp = zimmertyp ?? null;
    },
    onTeiltKontingentInput(zimmerkontingent, zimmertyp) {
      zimmerkontingent.teiltkontingentMit = zimmertyp ?? null;
      if (zimmerkontingent.teiltkontingentMit) {
        this.onTeiltKontingentSelected(zimmerkontingent);
      }
    },
    addPlanzimmerkontingentRow() {
      this.isEditing = true;
      this.editedSeason.planZimmerkontingente = [
        ...this.editedSeason.planZimmerkontingente,
        { zimmertyp: null, anzahlEinheiten: null, anzahlGeteiltesKontingent: null, teiltkontingentMit: null },
      ];
    },
    removePlanzimmerkontingentRow(index) {
      const zimmerkontingent = this.editedSeason.planZimmerkontingente[index];
      // bereits gespeicherte Zimmertypen erst nach Bestätigung entfernen
      if (zimmerkontingent?.id) {
        this.pendingRemoveIndex = index;
        this.pendingRemoveLabel =
          this.zimmertypOption(zimmerkontingent.zimmertyp)?.text ?? zimmerkontingent.zimmertyp;
        this.$bvModal.show('modalZimmertypLoeschen');
        return;
      }
      this.removeRowAtIndex(index);
    },
    async confirmRemovePlanzimmerkontingentRow() {
      const index = this.pendingRemoveIndex;
      this.pendingRemoveIndex = null;
      this.pendingRemoveLabel = '';
      this.$bvModal.hide('modalZimmertypLoeschen');
      if (index === null) return;
      // bereits gespeicherte Zimmertypen werden direkt serverseitig gelöscht
      await this.deletePlanzimmerkontingentRow(index);
    },
    // Löscht ein bereits gespeichertes Zimmerkontingent direkt am Server und aktualisiert den lokalen Stand
    async deletePlanzimmerkontingentRow(index) {
      const zimmerkontingent = this.editedSeason.planZimmerkontingente[index];
      if (!zimmerkontingent?.id) {
        this.removeRowAtIndex(index);
        return;
      }
      const reisekuerzel = this.reise.reisekuerzel;
      const gueltigAb = this.activeSaisonGueltigAb;
      this.isLoading = true;
      try {
        const response = await zimmerkontingenteService.remove(reisekuerzel, gueltigAb, zimmerkontingent.id);
        if (this.activeMetadata) {
          this.activeMetadata.planZimmerkontingente = (
            this.activeMetadata.planZimmerkontingente ?? []
          ).filter(item => item.id !== zimmerkontingent.id);
        }
        this.toast('Erfolgreich gelöscht.');
        const notUpdatedReisetermine = response?.data?.result?.value?.notUpdatedReisetermine ?? [];
        if (notUpdatedReisetermine.length > 0) {
          this.$emit('showNotUpdatedZimmerkontingente', notUpdatedReisetermine);
        }
        this.syncEditedFromActive();
      } catch {
        this.syncEditedFromActive();
      } finally {
        this.isLoading = false;
      }
    },
    removeRowAtIndex(index) {
      this.editedSeason.planZimmerkontingente = this.editedSeason.planZimmerkontingente.filter(
        (_, i) => i !== index
      );
    },
    // Die geteilte Anzahl kann maximal so groß sein wie die Anzahl des Zimmertyps
    getGeteiltesKontingentOptions(zimmerkontingent) {
      const max = zimmerkontingent.anzahlEinheiten ?? 0;
      return Array.from({ length: max }, (_, i) => ({ value: i + 1, text: String(i + 1) }));
    },
    // Beim Setzen eines geteilten Zimmertyps wird eine Standardanzahl vorbelegt
    onTeiltKontingentSelected(zimmerkontingent) {
      if (zimmerkontingent.anzahlGeteiltesKontingent == null) {
        zimmerkontingent.anzahlGeteiltesKontingent = 1;
      }
    },
    onAnzahlEinheitenInput(zimmerkontingent, anzahl) {
      zimmerkontingent.anzahlEinheiten = anzahl;
      this.onAnzahlEinheitenChange(zimmerkontingent);
    },
    // Bei Verkleinerung der Anzahl wird die geteilte Anzahl auf das neue Maximum begrenzt
    onAnzahlEinheitenChange(zimmerkontingent) {
      if (
        zimmerkontingent.anzahlGeteiltesKontingent != null &&
        zimmerkontingent.anzahlGeteiltesKontingent > zimmerkontingent.anzahlEinheiten
      ) {
        zimmerkontingent.anzahlGeteiltesKontingent = zimmerkontingent.anzahlEinheiten;
      }
    },
    clearTeiltKontingent(zimmerkontingent) {
      zimmerkontingent.teiltkontingentMit = null;
      zimmerkontingent.anzahlGeteiltesKontingent = null;
    },
    async savePlanZimmerkontingente() {
      const { toCreate, toUpdate, toDelete } = this.planZimmerkontingenteDiff;
      const reisekuerzel = this.reise.reisekuerzel;
      const gueltigAb = this.activeSaisonGueltigAb;
      this.isLoading = true;
      try {
        const createResponses = await Promise.all(
          toCreate.map(zimmerkontingent =>
            zimmerkontingenteService.create(reisekuerzel, gueltigAb, zimmerkontingent)
          )
        );
        const updateResponses = await Promise.all(
          toUpdate.map(zimmerkontingent =>
            zimmerkontingenteService.update(reisekuerzel, gueltigAb, zimmerkontingent)
          )
        );
        await Promise.all(
          toDelete.map(zimmerkontingent =>
            zimmerkontingenteService.remove(reisekuerzel, gueltigAb, zimmerkontingent.id)
          )
        );
        this.toast('Erfolgreich bearbeitet.');
        this.isEditing = false;
        const createdItems = createResponses.map(response => response.data.result.value.planZimmerkontingent);
        const updatedItems = updateResponses.map(response => response.data.result.value.planZimmerkontingent);
        // Reisetermine, deren abweichendes Zimmerkontingent nicht überschrieben wurde
        const notUpdatedReisetermine = [
          ...new Set(
            [...createResponses, ...updateResponses].flatMap(
              response => response.data.result.value.notUpdatedReisetermine ?? []
            )
          ),
        ];
        if (notUpdatedReisetermine.length > 0) {
          this.$emit('showNotUpdatedZimmerkontingente', notUpdatedReisetermine);
        }
        const deletedIds = toDelete.map(zimmerkontingent => zimmerkontingent.id);
        if (this.activeMetadata) {
          const remaining = (this.activeMetadata.planZimmerkontingente ?? [])
            .filter(item => !deletedIds.includes(item.id))
            .map(item => updatedItems.find(updated => updated.id === item.id) ?? item);
          this.activeMetadata.planZimmerkontingente = [...remaining, ...createdItems];
        }
        this.syncEditedFromActive();
      } catch {
        this.syncEditedFromActive();
      } finally {
        this.isLoading = false;
      }
    },
    // Verwirft alle Änderungen
    cancelPlanZimmerkontingente() {
      this.isEditing = false;
      this.syncEditedFromActive();
    },
    syncEditedFromActive() {
      this.editedSeason.planZimmerkontingente = (this.activeMetadata?.planZimmerkontingente ?? []).map(
        zimmerkontingent => ({ ...zimmerkontingent })
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';

.zimmerkontingent-table {
  margin-bottom: 4px;
}

.zimmerkontingent-head,
.zimmerkontingent-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px minmax(0, 1fr) 40px;
  column-gap: 12px;
  align-items: center;
}

.zimmerkontingent-head {
  padding: 8px 4px;
  border-bottom: 1px solid var(--border);
  font-size: $form-label-font-size;
  font-weight: $form-label-font-weight;
  color: $dark-75;
}

.zimmerkontingent-row {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f1f4;
  transition: background-color 0.15s ease;
}

.zimmerkontingent-row:hover {
  background-color: #fafbfc;
}

.zimmerkontingent-select,
.zimmerkontingent-anzahl {
  --app-select-height: 33px;
}

/* Die Anzahl steht in einer 84px breiten Spalte */
.zimmerkontingent-anzahl {
  --app-select-padding-x: 6px;
  --app-select-font-size: 0.9rem;
}

.zimmerkontingent-actions {
  display: flex;
  justify-content: flex-end;
}

.zimmerkontingent-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 0;
  border-radius: 6px;
  background-color: transparent;
  color: var(--text-subtle);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.zimmerkontingent-icon-btn:hover {
  background-color: #fff0f2;
  color: var(--danger);
}

.zimmerkontingent-share {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-height: 33px;
  padding: 4px 6px 4px 12px;
  border: 1px solid rgba(54, 153, 255, 0.25);
  border-radius: 8px;
  background-color: rgba(54, 153, 255, 0.1);
}

.zimmerkontingent-share-code {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--primary);
}

.zimmerkontingent-share-label {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.zimmerkontingent-share-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  border-radius: 6px;
  background-color: transparent;
  color: var(--primary);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.zimmerkontingent-share-x:hover {
  background-color: rgba(54, 153, 255, 0.18);
}

.zimmerkontingent-share-count {
  --app-select-width: auto;
  --app-select-height: 24px;
  --app-select-padding-x: 6px;
  --app-select-border-color: transparent;
  --app-select-radius: 6px;
  --app-select-background: var(--surface);
  --app-select-shadow: none;
  --app-select-color: var(--primary);
  --app-select-font-weight: 600;
  --app-select-font-size: 0.9rem;
}

.zimmerkontingent-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.save-btn:disabled {
  cursor: default;
  pointer-events: all;
  opacity: 1;
  background-color: #e4e6ef;
  border-color: #e4e6ef;
  color: var(--text-subtle);
}

.zimmerkontingent-empty {
  padding: 24px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  text-align: center;
  font-size: $form-label-font-size;
  font-weight: $form-label-font-weight;
  color: $dark-75;
}
</style>
